import { formatPairingCode } from '@forgesworn/joystick-protocol';
/**
 * pad.forgesworn.dev entry. Parses the pairing token from the URL
 * (?s=<sessionId>&w=<wsUrl>), shows a one-tap fullscreen gate on
 * mobile (so the user gesture captures fullscreen / install), then
 * connects via the joystick-mobile SDK and renders the controller
 * layout the host sends.
 */

import './style.css';
import { startControllerClient, type ControllerClient, type ControllerClientStatus, type ControllerSpec, type PairingToken, type SpectatorViewFrame } from '@forgesworn/joystick-mobile';
import { renderControls, type SlotInput } from './controls.js';
import { hasFullscreenAPI, isIosSafari, isMobileViewport, isStandalone, tryEnterFullscreen } from './install-fullscreen.js';
import { renderScanner } from './scanner.js';
import { clearPendingSession, openInstallGuide, readPendingSession, renderInstallHint, savePendingSession } from './install-first.js';
import { addScore, loadTournament, renderTournamentPanel, setName, showScoreToast } from './tournament.js';
import { DEFAULT_INITIALS, loadAccount, loadInitials, type Account } from './identity.js';
import { renderFirstRun, renderInitialsEntry, renderProfileChip } from './account-ui.js';
import { consumeSignetRedirect } from './signet.js';
import { installVisualViewportFit } from './visual-viewport-fit.js';
import { recoveryOptions } from './recovery.js';
import { parsePairingInput, pairingPath } from './pairing.js';

// We still suppress pinch zoom where browsers allow it. Two-thumb play
// (the joystick thumb plus a button) is exactly what iOS can read as a
// pinch. CSS touch-action:none and the viewport meta do not reliably
// stop it on iOS, so cancel the default on Safari's proprietary gesture
// events AND on every multi-touch move. The visualViewport fitter below
// makes accidental zoom survivable; these guards keep the page from
// entering that state in the first place when possible.
for (const gestureEvt of ['gesturestart', 'gesturechange', 'gestureend']) {
  document.addEventListener(gestureEvt, (e) => e.preventDefault(), { passive: false });
}
document.addEventListener('touchmove', (e) => {
  if (e.touches.length > 1) e.preventDefault();
}, { passive: false });

/** Map a vibration pattern's total duration to a coloured border flash.
 *  Gives iOS users (who can't feel navigator.vibrate) a sympathetic
 *  visual cue every time the host fires a haptic. */
function flashHapticBorder(pattern: number[]): void {
  // Total milliseconds of buzz time picks the colour bucket.
  const totalMs = pattern.reduce((acc, ms, i) => acc + (i % 2 === 0 ? ms : 0), 0);
  let colour = '#cfd8ff';     // default — soft blue
  if (totalMs >= 200) colour = '#ff6a6a';      // fail / hit — red
  else if (totalMs >= 100) colour = '#ffd84a'; // win — gold
  else if (totalMs >= 60) colour = '#b48cff';  // thud — purple
  else if (totalMs >= 25) colour = '#5fdbff';  // pulse — cyan
  // else 'tap' — soft blue
  const el = document.body;
  el.style.setProperty('--haptic-flash-colour', colour);
  el.classList.remove('haptic-flash');
  // Force reflow so removing + re-adding restarts the CSS animation
  // (handles back-to-back haptics without skipping a frame).
  void el.offsetWidth;
  el.classList.add('haptic-flash');
}

const DEFAULT_WS = import.meta.env.VITE_CONTROLLER_WS ?? 'wss://controller.pallasite.app/';

const app = document.getElementById('app');
if (!app) throw new Error('app root missing');
installVisualViewportFit(app);

const token = parsePairingToken();
const statusEl = makeStatusBar();
// The player's account: a guest, or someone who signed in with their own
// Nostr account (a convenience on this phone only). Resolved
// asynchronously at boot (a signet redirect return may need consuming
// first). The name that reaches a game is always the player's three
// arcade initials. activeClient is the live session client, if any, so
// a mid-session initials change can re-send the phone context.
let account: Account | null = null;
let activeClient: ControllerClient | null = null;

const boot = (): void => {
  if (!token) {
    // No session id in the URL. If the installed PWA was just opened
    // and a pair is pending (the user installed mid-game), resume
    // straight into it so an iPhone user never has to scan twice.
    // Consumed once so a later Back to the scanner does not loop.
    const pending = readPendingSession();
    if (isStandalone() && pending) {
      clearPendingSession();
      window.location.assign(pairingPath(pending));
      return;
    }
    // Otherwise surface the in-app QR scanner.
    renderScanner(app, account);
  } else if (shouldGate()) {
    // Browser-first: a mobile visitor with a pairing token gets the
    // controller in their browser, behind a one-tap splash. No install
    // gate — installing the PWA is offered as an optional upgrade via
    // the hint on that splash.
    renderGate(app, token, () => startSession(app, token, statusEl));
  } else {
    // Standalone PWA or desktop hitting a deep link: render the
    // controller straight away.
    clearPendingSession();
    startSession(app, token, statusEl);
  }
};

// Account resolution runs before anything renders. A same-tab return
// from signet-app is consumed first (a fresh sign-in wins), then the
// stored account, then first-run for a brand-new phone.
void (async (): Promise<void> => {
  account = await consumeSignetRedirect();
  if (!account) account = loadAccount();
  if (account && loadInitials()) {
    // Keep the cross-game tournament cookie's name in step.
    setName(loadInitials()!);
    boot();
  } else if (account) {
    // A signed-in or older account with no initials yet: ask once.
    renderInitialsEntry(app, (initials) => {
      setName(initials);
      boot();
    });
  } else {
    renderFirstRun(app, (acc) => {
      account = acc;
      setName(loadInitials() ?? DEFAULT_INITIALS);
      boot();
    });
  }
})();

killServiceWorker();
if (new URLSearchParams(location.search).get('diag') === '1') mountDiag();

function parsePairingToken(): PairingToken | null {
  const parsed = parsePairingInput(window.location.href);
  if (!parsed) return null;
  if (!new URLSearchParams(window.location.search).has('w')) parsed.ws = DEFAULT_WS;
  return parsed;
}

function shouldGate(): boolean {
  if (isStandalone()) return false;          // already installed → trust the chrome
  if (!isMobileViewport()) return false;     // desktop tester
  return true;
}

function renderGate(root: HTMLElement, token: PairingToken, onContinue: () => void): void {
  const gate = document.createElement('div');
  gate.className = 'gate';
  const logo = document.createElement('div'); logo.className = 'logo'; logo.textContent = 'JOYSTICK PAD';
  const session = document.createElement('div');
  session.className = 'session';
  session.innerHTML = `SESSION <code>${escapeHtml(formatPairingCode(token.sessionId))}</code>`;

  const go = document.createElement('button');
  go.className = 'go';
  go.textContent = 'TAP TO PLAY';

  gate.appendChild(logo);
  gate.appendChild(session);
  gate.appendChild(go);
  // Optional install upgrade — a compact, dismissable hint, never a gate.
  renderInstallHint(gate);
  const apps = document.createElement('a'); apps.href = `/get.html${pairingPath(token).slice(1)}`; apps.textContent = 'Controller app options'; gate.appendChild(apps);

  root.appendChild(gate);

  // The tap runs inside the user gesture so a one-shot fullscreen
  // request is allowed (Android honours it, iOS no-ops). No fullscreen
  // chasing beyond this — the controller is happy in browser chrome.
  const onTap = (): void => {
    tryEnterFullscreen();
    gate.remove();
    onContinue();
  };
  go.addEventListener('click', onTap);
  // Whole-screen tap also dismisses, in case the button is missed —
  // but not taps on the install hint's own controls.
  gate.addEventListener('click', (e) => {
    if (e.target !== go && !(e.target instanceof HTMLElement && e.target.closest('button,a'))) {
      onTap();
    }
  });
}

/** Clear the service worker + caches and hard-reload. Used when a
 *  stale bundle is haunting us between deploys. */
async function forceRefresh(): Promise<void> {
  if (!confirm('Force refresh? Clears the offline cache and reloads.')) return;
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const r of regs) await r.unregister();
    }
    if ('caches' in window) {
      const names = await caches.keys();
      for (const n of names) await caches.delete(n);
    }
  } catch {
    /* fall through to a plain reload */
  }
  // Bust the URL cache too with a no-op query param.
  const url = new URL(window.location.href);
  url.searchParams.set('_ts', String(Date.now()));
  window.location.replace(url.toString());
}

function makeStatusBar(): HTMLElement {
  const bar = document.createElement('div');
  bar.className = 'statusbar waiting';
  const dot = document.createElement('span'); dot.className = 'dot';
  const name = document.createElement('span'); name.className = 'name'; name.textContent = 'JOYSTICK PAD';
  const status = document.createElement('span'); status.className = 'status'; status.textContent = 'WAITING';
  const left = document.createElement('span'); left.className = 'left';
  left.appendChild(dot);
  left.appendChild(name);
  // Long-press the identity label to force-refresh. Keeps the rare,
  // destructive action available without an always-on button that
  // can be fat-fingered.
  left.title = 'Hold to force refresh';
  let lpTimer: number | null = null;
  const cancelLp = (): void => {
    if (lpTimer !== null) { clearTimeout(lpTimer); lpTimer = null; }
  };
  left.addEventListener('pointerdown', () => {
    cancelLp();
    lpTimer = window.setTimeout(() => { lpTimer = null; void forceRefresh(); }, 650);
  });
  left.addEventListener('pointerup', cancelLp);
  left.addEventListener('pointerleave', cancelLp);
  left.addEventListener('pointercancel', cancelLp);

  const right = document.createElement('span'); right.className = 'right';
  // Back button — drops the current session and returns to the
  // scanner home so the user can scan a different game's QR. Only
  // visible once a session URL is present (added by startSession).
  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'statusbar-btn back';
  back.title = 'Scan another game';
  back.setAttribute('aria-label', 'Scan another game');
  back.textContent = '⟵';
  back.style.display = 'none';
  back.addEventListener('click', () => {
    // Navigate to the bare path — no session param means startSession
    // won't run; renderScanner takes over.
    clearPendingSession();
    activeClient?.close();
    window.location.assign('/');
  });
  // Force-refresh button — only surfaced when the connection has
  // actually dropped (reconnecting / closed), which is the one time
  // a non-dev user would reach for it. Hidden the rest of the time
  // so it can't be mis-tapped; long-press the label otherwise.
  const refresh = document.createElement('button');
  refresh.type = 'button';
  refresh.className = 'statusbar-btn refresh';
  refresh.title = 'Force refresh — clears the offline cache';
  refresh.setAttribute('aria-label', 'Force refresh');
  refresh.textContent = '↻';
  refresh.style.display = 'none';
  refresh.addEventListener('click', () => { void forceRefresh(); });
  right.appendChild(back);
  right.appendChild(refresh);
  // iPhone Safari has no fullscreen API — the installed PWA is the
  // only chrome-free controller. Surface a persistent shortcut into
  // the Add-to-Home-Screen guide so it is reachable mid-game.
  if (isIosSafari() && !isStandalone()) {
    const fs = document.createElement('button');
    fs.type = 'button';
    fs.className = 'statusbar-btn fullscreen';
    fs.title = 'Add to Home Screen for a full-screen controller';
    fs.setAttribute('aria-label', 'Play fullscreen');
    fs.textContent = '⤢';
    fs.addEventListener('click', () => openInstallGuide());
    right.appendChild(fs);
  }
  bar.appendChild(left);
  bar.appendChild(status);
  bar.appendChild(right);
  return bar;
}

function setStatus(bar: HTMLElement, s: ControllerClientStatus): void {
  const status = bar.querySelector('.status');
  if (status) status.textContent = s.kind === 'full' ? 'SESSION FULL' : s.kind.toUpperCase();
  bar.classList.remove('connecting', 'waiting', 'paired', 'reconnecting', 'full', 'closed');
  bar.classList.add(s.kind);
  // Surface the force-refresh button only while the link is down —
  // that's the moment a normal user would want it. Long-press on the
  // label covers the dev-phase stale-bundle case the rest of the time.
  const refresh = bar.querySelector('.statusbar-btn.refresh') as HTMLElement | null;
  if (refresh) {
    const linkDown = s.kind === 'reconnecting' || s.kind === 'closed';
    refresh.style.display = linkDown ? 'inline-flex' : 'none';
  }
}

function setSpecName(bar: HTMLElement, name: string): void {
  const nameEl = bar.querySelector('.name');
  if (nameEl) nameEl.textContent = name;
}

/** Push the tournament total + the player's initials to the host as
 *  phone context. The host shows "entered with N pts" / the initials.
 *  The initials are the only name the pad ever sends. */
function pushContext(client: ControllerClient): void {
  const t = loadTournament();
  const initials = loadInitials();
  client.setPhoneContext({
    tournamentTotal: t.total,
    ...(initials ? { name: initials } : {}),
  });
}

/** Called after the initials change: keep the tournament cookie in step
 *  and re-send the phone context so a live game picks them up now. */
function onAccountChanged(): void {
  const initials = loadInitials();
  if (!initials) return;
  setName(initials);
  if (activeClient) pushContext(activeClient);
}

function startSession(root: HTMLElement, token: PairingToken, status: HTMLElement, admittedClient?: ControllerClient): void {
  root.replaceChildren(status);
  // Reveal the back button now that there's a session to leave.
  const back = status.querySelector('.statusbar-btn.back') as HTMLElement | null;
  if (back) back.style.display = 'inline-flex';
  const idle = document.createElement('div');
  idle.className = 'idle-message';
  idle.innerHTML = `
    <h1>WAITING FOR GAME</h1>
    <p>Session <code>${escapeHtml(formatPairingCode(token.sessionId))}</code></p>
    <p>Keep this page open. The game will appear here when it pairs.</p>
  `;

  const client: ControllerClient = admittedClient ?? startControllerClient(token, recoveryOptions(token));
  activeClient = client;
  let disposeControls: (() => void) | null = null;
  let lastSpectatorView: SpectatorViewFrame | null = null;
  let feedbackLocked = false;
  const feedback = document.createElement('div');
  feedback.className = 'controller-feedback'; feedback.setAttribute('role', 'status');
  feedback.setAttribute('aria-live', 'polite'); feedback.hidden = true;
  const retry = document.createElement('button');
  retry.className = 'btn primary';
  retry.textContent = 'TRY AGAIN';
  retry.addEventListener('click', () => window.location.reload());
  // Hold this session so a mid-game PWA install resumes straight back
  // into it. Only while in a browser tab — in the installed app there
  // is no install handoff, and saving there would make a Back to the
  // scanner immediately bounce back here.
  if (!isStandalone()) savePendingSession(token);
  // Send the initials + running total up front. The SDK re-sends them
  // on every pair-up and on reconnect.
  pushContext(client);

  // Fullscreen keep-alive (Android). A rotation can drop fullscreen
  // and the page cannot re-grab it on the rotation event itself —
  // requestFullscreen needs a user gesture. So re-assert it on the
  // next control touch, which is a gesture. tryEnterFullscreen no-ops
  // when already fullscreen, and where there is no API (iPhone) the
  // listener is never installed.
  if (hasFullscreenAPI() && !isStandalone()) {
    document.addEventListener('pointerdown', () => tryEnterFullscreen());
  }

  // Idle / waiting screen: profile chip (tap to change initials) +
  // the tournament panel. Mounted now and re-mounted whenever the
  // session drops back out of a game.
  const mountIdle = (): void => {
    disposeControls?.();
    disposeControls = null;
    root.replaceChildren(status);
    root.classList.remove('home-mode', 'pad-mode', 'tilt-mode');
    root.classList.add('idle-mode');
    root.appendChild(idle);
    if (account) renderProfileChip(root, account, onAccountChanged);
    renderTournamentPanel(root, () => pushContext(client));
  };
  mountIdle();

  const send: SlotInput = (slot, value) => {
    if (!feedbackLocked || value === '0' || slot.endsWith('-end')) client.sendInput(slot, value);
  };
  client.onFeedback((frame) => {
    feedbackLocked = frame.locked === true;
    feedback.textContent = frame.message; feedback.hidden = !frame.message;
    feedback.dataset.tone = frame.tone ?? 'neutral';
    root.querySelectorAll<HTMLElement>('[data-control-slot]').forEach(button => {
      button.classList.toggle('selected', !!frame.selected && button.dataset.controlSlot === frame.selected);
      button.setAttribute('aria-disabled', String(feedbackLocked));
    });
    if (!root.contains(feedback)) status.after(feedback);
  });
  client.onStatus((s) => {
    setStatus(status, s);
    if (s.kind !== 'paired') {
      feedbackLocked = false; feedback.hidden = true;
      lastSpectatorView = null;
      if (!root.contains(idle)) mountIdle();
      const title = idle.querySelector('h1');
      const detail = idle.querySelector('p:last-of-type');
      if (title) title.textContent = s.kind === 'full' ? 'THIS GAME IS FULL' : s.kind === 'reconnecting' ? 'RECONNECTING' : 'WAITING FOR GAME';
      if (detail) detail.textContent = s.kind === 'full'
        ? 'All phone slots are taken. Try again when someone leaves, or scan another game.'
        : s.kind === 'reconnecting' ? 'Keep this page open. Your place is held briefly while the connection returns.'
        : 'Keep this page open. The game will appear here when it pairs.';
      if (s.kind === 'full') idle.appendChild(retry);
      else retry.remove();
    }
  });

  client.onSpec((spec) => {
    disposeControls?.();
    setSpecName(status, spec.name);
    disposeControls = renderControls(root, spec, send, () => status);
    status.after(feedback);
    // Re-mount spectator card if we have one in memory (the spec
    // rebuild blows the DOM away). Otherwise leave the controls alone.
    if (lastSpectatorView) renderSpectatorOverlay(root, lastSpectatorView);
  });

  client.onWelcome((slot) => {
    setSpecName(status, `P${slot + 1} · ${status.querySelector('.name')?.textContent ?? 'JOYSTICK PAD'}`);
  });

  // Game awarded points at end of a round — accumulate into the
  // running tournament total and float a toast. Re-announce the new
  // total so the host can update its scoreboard if it cares to.
  client.onGameScore((score, label) => {
    const next = addScore(Math.max(0, score));
    showScoreToast(Math.max(0, score), label, next.total);
    pushContext(client);
  });

  // Spectator-view frames — phones beyond the player cap receive a
  // periodic leaderboard from the game. Render a card above the
  // (typically very minimal) controls so the audience can follow along.
  client.onSpectatorView((view) => {
    lastSpectatorView = view;
    renderSpectatorOverlay(root, view);
  });

  // Mirror every haptic the SDK honours into a coloured border flash.
  // On Android the user gets both vibration and the flash; on iOS the
  // flash is the only feedback (Safari ignores navigator.vibrate).
  client.onHaptic((pattern) => flashHapticBorder(pattern));
}

function renderSpectatorOverlay(root: HTMLElement, view: SpectatorViewFrame): void {
  const existing = root.querySelector('.spectator-overlay');
  if (existing) existing.remove();
  const card = document.createElement('div');
  card.className = 'spectator-overlay';
  const head = document.createElement('div');
  head.className = 'spectator-head';
  const headLabel = document.createElement('div');
  headLabel.className = 'spectator-head-label';
  headLabel.textContent = 'SPECTATOR';
  const headTitle = document.createElement('div');
  headTitle.className = 'spectator-head-title';
  headTitle.textContent = view.title;
  head.appendChild(headLabel);
  head.appendChild(headTitle);
  card.appendChild(head);
  if (view.highlight) {
    const h = document.createElement('div');
    h.className = 'spectator-highlight';
    h.textContent = view.highlight;
    card.appendChild(h);
  }
  const list = document.createElement('div');
  list.className = 'spectator-list';
  for (const row of view.leaderboard.slice(0, 8)) {
    const r = document.createElement('div');
    r.className = 'spectator-row';
    const accent = document.createElement('span');
    accent.className = 'spectator-accent';
    accent.style.background = row.colour;
    const lbl = document.createElement('span');
    lbl.className = 'spectator-label';
    lbl.textContent = row.label;
    const sc = document.createElement('span');
    sc.className = 'spectator-score';
    sc.textContent = String(row.score);
    r.appendChild(accent);
    r.appendChild(lbl);
    r.appendChild(sc);
    if (typeof row.tournamentTotal === 'number') {
      const tt = document.createElement('span');
      tt.className = 'spectator-total';
      tt.textContent = `· Σ ${row.tournamentTotal}`;
      r.appendChild(tt);
    }
    list.appendChild(r);
  }
  card.appendChild(list);
  // Insert at the very top of the controller-mode root so it stays
  // visible above any face / joystick layout.
  root.insertBefore(card, root.firstChild);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

/** The pad no longer ships a service worker — a cache-first worker had
 *  been pinning a stale app shell, so deploys never reached the device.
 *  Unregister any worker still installed; sw.js itself now self-
 *  destructs and wipes its caches. */
function killServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  void navigator.serviceWorker.getRegistrations()
    .then((regs) => { for (const reg of regs) void reg.unregister(); })
    .catch(() => { /* ignore */ });
}

/** Opt-in viewport/layout readout for device checks: append ?diag=1. */
function mountDiag(): void {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;'
    + 'padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);';
  document.body.appendChild(probe);

  const box = document.createElement('div');
  box.style.cssText =
    'position:fixed;top:0;left:0;z-index:99999;pointer-events:none;white-space:pre;'
    + 'font:9px/1.4 ui-monospace,Menlo,monospace;color:#5fff5f;'
    + 'background:rgba(0,0,0,0.85);padding:5px 7px;border-bottom-right-radius:8px;';
  document.body.appendChild(box);

  const rectOf = (sel: string): string => {
    const el = document.querySelector(sel);
    if (!el) return `${sel}: absent`;
    const r = el.getBoundingClientRect();
    const htmlEl = el instanceof HTMLElement ? el : null;
    const logical = htmlEl ? ` log ${htmlEl.offsetWidth}x${htmlEl.offsetHeight}` : '';
    return `${sel} ${Math.round(r.left)},${Math.round(r.top)} ${Math.round(r.width)}x${Math.round(r.height)}${logical}`;
  };
  const tick = (): void => {
    const cs = getComputedStyle(probe);
    const vv = window.visualViewport;
    const rootCs = getComputedStyle(document.documentElement);
    box.textContent = [
      `win ${window.innerWidth}x${window.innerHeight} dpr ${window.devicePixelRatio}`,
      `vv ${vv ? `${Math.round(vv.width)}x${Math.round(vv.height)} sc${vv.scale.toFixed(2)}` : 'absent'}`,
      `fit ${Math.round(parseFloat(rootCs.getPropertyValue('--pad-vv-width')) || 0)}x${Math.round(parseFloat(rootCs.getPropertyValue('--pad-vv-height')) || 0)} inv${(parseFloat(rootCs.getPropertyValue('--pad-vv-inverse-scale')) || 1).toFixed(2)}`,
      `ori ${screen.orientation?.type ?? '?'}`,
      `safe T ${cs.paddingTop} R ${cs.paddingRight} B ${cs.paddingBottom} L ${cs.paddingLeft}`,
      rectOf('#app'),
      rectOf('.main'),
      rectOf('.joystick'),
      rectOf('.face'),
    ].join('\n');
  };
  tick();
  window.setInterval(tick, 300);
}
